import { differenceInHours } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { head, last } from 'lodash';

import { Link } from '@/components/link';
import { VisuallyHidden } from '@/components/visually-hidden';
import { useTruncated } from '@/hooks/use-truncated';
import { DATE_MONTH_FORMAT } from '@/lib/constants';
import { SimpleScheduleInterface } from '@/types';
import { calculateScheduleTimelineBars } from '@/utils/schedules';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { SimpleScheduleRow } from './row';

const MAX_ROWS = 5;
const ROUNDED_HOURS = 12;

interface Props {
  schedules: SimpleScheduleInterface;
  carrierName: string;
}

const SimpleSchedules = ({ schedules, carrierName }: Props) => {
  // Sort by departure time
  const schedulesByDepartureTime = [...schedules.routes].sort((a, b) => {
    return new Date(a.departureDateTime).valueOf() - new Date(b.departureDateTime).valueOf();
  });

  // Sort by arrival time
  const schedulesByArrivalTime = [...schedules.routes].sort((a, b) => {
    return new Date(a.arrivalDateTime).valueOf() - new Date(b.arrivalDateTime).valueOf();
  });

  const { rows, shouldShowLoadMore, shouldShowAllRows, showAllRows, remainingRows } = useTruncated(
    schedulesByDepartureTime,
    MAX_ROWS,
  );

  if (!schedules.routes.length) return null;
  // Get first departure and last arrival to calculate timeline bars
  const firstRow = head(schedulesByDepartureTime)!;

  const firstDepartureDate = firstRow.departureDateTime ? new Date(firstRow.departureDateTime) : null;
  const lastArrival = last(schedulesByArrivalTime);
  const lastArrivalDate = lastArrival?.arrivalDateTime ? new Date(lastArrival.arrivalDateTime) : null;
  const originTimezone = firstRow.from?.timezoneName;

  // Calculate time scale to draw
  const hoursBetween =
    lastArrivalDate && firstDepartureDate ? differenceInHours(lastArrivalDate, firstDepartureDate) : 0;
  const timeRange = hoursBetween < 0 ? ROUNDED_HOURS : Math.ceil(hoursBetween / ROUNDED_HOURS) * ROUNDED_HOURS;

  // Calculate Bars
  const { bars } = firstDepartureDate ? calculateScheduleTimelineBars(timeRange, firstDepartureDate) : { bars: [] };

  const handleShowMoreRows = () => {
    showAllRows();
    // Analytics Event
    trackUser.event('See more schedules (load more)', {
      carrier: carrierName,
    });
  };

  const handleExploreMore = () => {
    // Analytics Event
    trackUser.event('See more schedules (redirect to /search)', {
      carrier: carrierName,
    });
  };

  const { base, table, caption, tr, th, barsStyles, bar, barLine, dateCell, buttonLink } = styles();

  return (
    <div>
      <div className={base()}>
        <table className={table()}>
          <caption className={caption()}>{carrierName} schedules and upcoming departures</caption>
          <thead>
            <tr className={tr()}>
              <th className={th({ className: 'w-[15%]' })}>Next Departure</th>
              <th className={th({ className: 'w-[10%]' })}>Arriving in</th>
              <th className={th({ className: 'w-[10%]' })}>Duration</th>
              <th className={th({ className: 'w-[15%]' })}>Vessel</th>
              <th className={th({ className: 'w-[35%]' })}>
                <VisuallyHidden as="span">Description</VisuallyHidden>
                <div className={barsStyles()}>
                  {bars.map((dateTime) => (
                    <div key={dateTime.toString()} className={bar()}>
                      <div className={barLine()} />
                      <div className={dateCell()}>
                        {originTimezone ? formatInTimeZone(dateTime, originTimezone, DATE_MONTH_FORMAT) : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </th>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <th className={th({ className: 'w-[5%]' })} />
            </tr>
          </thead>
          <tbody>
            {firstDepartureDate
              ? rows.map((row) => (
                  <SimpleScheduleRow key={row.id} row={row} timeRange={timeRange} rangeStartTime={firstDepartureDate} />
                ))
              : null}
          </tbody>
        </table>
      </div>
      {shouldShowLoadMore && !shouldShowAllRows && (
        <button className={buttonLink()} onClick={() => handleShowMoreRows()} type="button">
          See more schedules (+{remainingRows})
        </button>
      )}
      {shouldShowAllRows && (
        <Link href="/search/schedules" className={buttonLink()} onClick={() => handleExploreMore()}>
          Explore more schedules and routes
        </Link>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative',
    table: 'h-px w-full border-separate border-spacing-0 border-spacing-y-2',
    caption: 'hidden',
    tr: 'grid grid-cols-2 md:table-row',
    th: 'mx-0 whitespace-nowrap p-0 px-4 text-left text-xs font-medium uppercase text-grey-800 max-md:[&:nth-child(3)]:hidden max-md:[&:nth-child(4)]:hidden max-md:[&:nth-child(5)]:hidden',
    barsStyles: 'hidden h-full md:grid md:auto-cols-fr md:grid-flow-col',
    bar: 'z-2 h-full',
    barLine: 'absolute bottom-0 top-2 border-l border-dashed border-grey-300',
    dateCell: 'mx-0 whitespace-nowrap p-0 px-4 text-xs font-medium uppercase text-grey-800',
    buttonLink:
      'ml-6 mt-2 border-b border-l-0 border-r-0 border-t-0 border-dotted border-grey-400 text-baseSm italic text-grey-500 hover:border-b-lightBlue-500 hover:text-lightBlue-500',
  },
});

export { SimpleSchedules };
